import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CircleDollarSign, ExternalLink, Image as ImageIcon, Mail, MessageCircle, Play } from 'lucide-react';
import {
  confirmSponsorView,
  createDonationSession,
  fetchPulseStatus,
  fetchSponsorCreative,
  postMicrositeEvents,
  resolveMicrosite,
  submitMicrositeLead,
} from './api';
import './PublicMicrosite.css';

const APP_HOSTS = (process.env.REACT_APP_IDAPPZ_APP_HOSTS || 'localhost,127.0.0.1')
  .split(',')
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean);
const SUPPORTED_PUBLIC_PRESETS = new Set(['stack', 'grid', 'swipe', 'spotlight', 'masonry', 'timeline']);
export const LINKBOARD_RUNTIME_HASH = 'linkboard-browser-runtime-2';
const SUPPORTED_BLOCK_TYPES = new Set([
  'hero',
  'announcement',
  'linkCollection',
  'socialStrip',
  'contactActions',
  'emailCapture',
  'donation',
  'youtubeEmbed',
  'spotifyEmbed',
  'postEmbed',
  'pulseCard',
  'richText',
  'divider',
  'spacer',
]);
const SUPPORTED_BACKGROUND_TYPES = new Set(['solid', 'gradient', 'blur', 'pattern', 'image']);
const SUPPORTED_BUTTON_SHAPES = new Set(['pill', 'rounded', 'sharp']);
const SUPPORTED_CARD_STYLES = new Set(['glass', 'solid', 'outline']);
const PUZZLE_GRID_COLUMNS = 4;
const PUZZLE_GRID_COLUMN_GAP = 12;
const PUZZLE_GRID_ROW_GAP = 22;
const PUZZLE_GRID_BOTTOM_BREATH = 34;
const PUZZLE_DRAG_HOLD_MS = 220;
const PUZZLE_DRAG_COMMIT_DISTANCE = 14;

const normalizePublicPreset = (preset) => {
  const resolved = preset || 'stack';
  if (!SUPPORTED_PUBLIC_PRESETS.has(resolved)) {
    throw new Error(`Unsupported LinkBoard preset: ${resolved}`);
  }
  return resolved;
};

const assertSupportedPublicDocument = (document) => {
  if (!document?.theme || !document?.display || !Array.isArray(document?.blocks)) {
    throw new Error('Invalid LinkBoard document.');
  }
  if (!SUPPORTED_BACKGROUND_TYPES.has(document.theme.backgroundType || 'gradient')) {
    throw new Error(`Unsupported background type: ${document.theme.backgroundType}`);
  }
  if (!SUPPORTED_BUTTON_SHAPES.has(document.theme.buttonShape || 'rounded')) {
    throw new Error(`Unsupported button shape: ${document.theme.buttonShape}`);
  }
  if (!SUPPORTED_CARD_STYLES.has(document.theme.cardStyle || 'glass')) {
    throw new Error(`Unsupported card style: ${document.theme.cardStyle}`);
  }
  normalizePublicPreset(document.display.defaultPreset || 'stack');
  (document.display.availablePresets || ['stack']).forEach(normalizePublicPreset);
  document.blocks.forEach((block) => {
    if (!SUPPORTED_BLOCK_TYPES.has(block.type)) {
      throw new Error(`Unsupported LinkBoard block type: ${block.type}`);
    }
    if (block.type === 'linkCollection') normalizePublicPreset(block.defaultPreset || document.display.defaultPreset || 'stack');
  });
};

const getLeadInputConfig = (captureType = 'email') => {
  if (captureType === 'phone') {
    return { type: 'tel', placeholder: 'Enter your phone number' };
  }
  if (captureType === 'username') {
    return { type: 'text', placeholder: 'Enter your username' };
  }
  return { type: 'email', placeholder: 'Enter your email' };
};

const sanitizeLinkHref = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('#')) return raw;
  if (/^\/(?!\/)/.test(raw)) return raw;
  if (/^(https?:\/\/|mailto:|tel:)/i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(?:[/?#].*)?$/i.test(raw)) return `https://${raw}`;
  return '';
};

const getLinkHref = (item) => sanitizeLinkHref(item?.trackedPath || item?.url || '');

const sanitizeEmbedUrl = (input) => {
  if (!input) return null;
  try {
    const url = new URL(input);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtu.be') {
      const videoId = host === 'youtu.be'
        ? url.pathname.replace('/', '')
        : url.searchParams.get('v') || url.pathname.split('/').pop();
      return videoId ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}` : null;
    }
    if (host === 'spotify.com' || host.endsWith('.spotify.com') || host === 'open.spotify.com') {
      return input
        .replace('/track/', '/embed/track/')
        .replace('/playlist/', '/embed/playlist/')
        .replace('/episode/', '/embed/episode/')
        .replace('/show/', '/embed/show/');
    }
    return null;
  } catch (_error) {
    return null;
  }
};

const buildActionHref = (action) => {
  switch (action.actionType) {
    case 'whatsapp':
      return `https://wa.me/${String(action.value).replace(/\D/g, '')}`;
    case 'email':
      return `mailto:${action.value}`;
    case 'phone':
      return `tel:${action.value}`;
    case 'location':
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(action.value)}`;
    default:
      return sanitizeLinkHref(action.value);
  }
};

const getInitials = (value = '') =>
  String(value)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'ID';

const resolveRenderableImageRef = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^(https?:\/\/|data:image\/)/i.test(raw)) return raw;
  if (/^\/(?!\/)/.test(raw)) return raw;
  if (/^asset:/i.test(raw)) return `/a/${encodeURIComponent(raw.replace(/^asset:/i, '').trim())}`;
  if (/^asset[-_]/i.test(raw)) return `/a/${encodeURIComponent(raw)}`;
  return '';
};

const clampNumber = (value, min, max, fallback) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return fallback;
  return Math.max(min, Math.min(max, next));
};

const normalizeTextKey = (value) => String(value || '').trim().toLowerCase();

const DEFAULT_PUBLIC_COPY = new Set([
  'new creator',
  'create your digital identity',
  'everything you need to grow online',
]);

const cleanDefaultPublicCopy = (value) => {
  const trimmed = String(value || '').trim();
  return trimmed && !DEFAULT_PUBLIC_COPY.has(normalizeTextKey(trimmed)) ? trimmed : '';
};

const isPlaceholderHeroCta = (label, href) => {
  const normalizedLabel = normalizeTextKey(label);
  const normalizedHref = String(href || '').trim().toLowerCase();
  if (!normalizedLabel) return true;
  if (normalizedLabel === 'explore' || normalizedLabel === 'open') {
    return !normalizedHref || normalizedHref === '#' || normalizedHref === 'https://idappz.com';
  }
  return false;
};

const isPhotoLinkItem = (item) =>
  Boolean(item?.imageUrl) ||
  normalizeTextKey(item?.icon) === 'image' ||
  String(item?.url || '').trim() === '#photo';

const getRenderableLinkHref = (item) => {
  const imageSrc = resolveRenderableImageRef(item?.imageUrl);
  if (isPhotoLinkItem(item) && imageSrc) return imageSrc;
  return getLinkHref(item);
};

const SOCIAL_META = {
  instagram: { label: 'Instagram', short: 'Ig', color: '#e1306c' },
  ig:        { label: 'Instagram', short: 'Ig', color: '#e1306c' },
  tiktok:    { label: 'TikTok',    short: 'Tt', color: '#ffffff' },
  tt:        { label: 'TikTok',    short: 'Tt', color: '#ffffff' },
  youtube:   { label: 'YouTube',   short: 'Yt', color: '#ff0000' },
  yt:        { label: 'YouTube',   short: 'Yt', color: '#ff0000' },
  whatsapp:  { label: 'WhatsApp',  short: 'Wa', color: '#25d366' },
  wa:        { label: 'WhatsApp',  short: 'Wa', color: '#25d366' },
  linkedin:  { label: 'LinkedIn',  short: 'in', color: '#0a66c2' },
  spotify:   { label: 'Spotify',   short: 'Sp', color: '#1db954' },
  twitter:   { label: 'X',         short: 'X',  color: '#1d9bf0' },
  x:         { label: 'X',         short: 'X',  color: '#1d9bf0' },
  facebook:  { label: 'Facebook',  short: 'Fb', color: '#1877f2' },
  github:    { label: 'GitHub',    short: 'Gh', color: '#ffffff' },
  twitch:    { label: 'Twitch',    short: 'Tw', color: '#9146ff' },
  discord:   { label: 'Discord',   short: 'Dc', color: '#5865f2' },
  pinterest: { label: 'Pinterest', short: 'Pi', color: '#e60023' },
  snapchat:  { label: 'Snapchat',  short: 'Sn', color: '#fffc00' },
  telegram:  { label: 'Telegram',  short: 'Tg', color: '#2ca5e0' },
};

const getSocialMeta = (network) => {
  const key = String(network || '').toLowerCase().trim();
  return SOCIAL_META[key] || { label: network || 'Link', short: String(network || 'Lk').slice(0, 2).toUpperCase(), color: 'var(--lb-accent)' };
};

// ─── Surface palette — matches mobile buildPreviewSurfacePalette logic ───────
const _hexToRgb = (hex) => {
  const n = (hex || '#000000').replace('#', '').replace(/^([0-9a-f]{3})$/i, (_, h) => h[0]+h[0]+h[1]+h[1]+h[2]+h[2]);
  return { r: parseInt(n.slice(0,2),16)||0, g: parseInt(n.slice(2,4),16)||0, b: parseInt(n.slice(4,6),16)||0 };
};
const _isLight = (hex) => { const {r,g,b} = _hexToRgb(hex); return (0.299*r+0.587*g+0.114*b)/255 > 0.68; };
const _mix = (a, b, t) => {
  const ca = _hexToRgb(a), cb = _hexToRgb(b), r = Math.max(0,Math.min(1,t));
  return '#'+[ca.r+(cb.r-ca.r)*r, ca.g+(cb.g-ca.g)*r, ca.b+(cb.b-ca.b)*r]
    .map((v) => Math.round(v).toString(16).padStart(2,'0')).join('');
};
const _rgba = (hex, a) => { const {r,g,b} = _hexToRgb(hex); return `rgba(${r},${g},${b},${parseFloat(a.toFixed(3))})`; };
const _ctaText = (ctaHex) => _isLight(ctaHex) ? '#0f172a' : '#ffffff';
const _classToken = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '');

const buildSurfaceVars = (theme) => {
  const accent = theme.accentColor || '#3b82f6';
  // Explicit overrides take absolute priority
  const card   = theme.cardSurfaceColor   || theme.surfaceColor;
  const frame  = theme.cardFrameColor     || theme.frameColor;
  const chip   = theme.chipSurfaceColor   || theme.surfaceColor;
  const chipFr = theme.chipFrameColor     || theme.frameColor;
  const cta    = theme.ctaSurfaceColor;
  const ctaFr  = theme.ctaFrameColor;
  const input  = theme.inputSurfaceColor  || theme.surfaceColor;
  const inputFr= theme.inputFrameColor    || theme.frameColor;

  // Map buttonStylePreset → special fixed palettes
  const p = theme.buttonStylePreset || (() => {
    const s = theme.buttonShape || 'pill', c = theme.cardStyle || 'glass';
    if (s==='pill'&&c==='glass') return 'softPill';
    if (s==='rounded'&&c==='outline') return 'glassOutline';
    if (s==='rounded'&&c==='solid') return 'solidBlock';
    if (s==='sharp') return 'sharpFrame';
    if (s==='pill'&&c==='solid') return 'accentGlow';
    return 'softPill';
  })();

  const fixed = {
    retroMarquee:   { card:'#140516',  frame:'#f2bd5b', chip:'#24102c', cta:'#35123d', ctaText:'#fff7df' },
    neobrutalism:   { card:'#ffe067',  frame:'#000000', chip:'#ff9ac2', cta:'#7ff0a6', ctaText:'#000000' },
    frutigerAero:   { card:'#9ae5ff',  frame:'rgba(255,255,255,0.36)', chip:'rgba(255,255,255,0.28)', cta:'#9fe8ff', ctaText:'#08345d' },
    claymorphism:   { card:'#e5edf5',  frame:'transparent', chip:'#f5f8fb', cta:'#d8e3ee', ctaText:'#415668' },
    cyberpunkGlitch:{ card:'#050505',  frame:'#00f6ff', chip:'#111111', cta:'#ffe600', ctaText:'#000000' },
    clearGlass:     { card:'rgba(255,255,255,0.08)', frame:'rgba(255,255,255,0.2)', chip:'rgba(255,255,255,0.14)', cta:'rgba(255,255,255,0.12)', ctaText:'#ffffff' },
    chromeMetal:    { card:'#cdcdcd',  frame:'#b6b6b6', chip:'#dedede', cta:'#d6d6d6', ctaText:'#111111' },
    dieterRams:     { card:'#F5F5F0',  frame:'#E0E0DB', chip:'#E0E0DB', cta:'#FF5000', ctaText:'#ffffff' },
    skeuomorphicRecessed:{ card:'#242424', frame:'#101010', chip:'#353535', cta:'#2c2c2c', ctaText:'#9aff86' },
    holographicFoil:{ card:'rgba(255,255,255,0.62)', frame:'rgba(255,255,255,0.6)', chip:'rgba(255,255,255,0.46)', cta:'rgba(255,255,255,0.5)', ctaText:'#243041' },
    prismShift:     { card:'rgba(244,248,255,0.64)', frame:'rgba(255,255,255,0.78)', chip:'rgba(255,255,255,0.52)', cta:'#e3fbff', ctaText:'#1d2a42' },
    velvetNoir:     { card:'#150916',  frame:'#967246', chip:'#241129', cta:'#2c1233', ctaText:'#fff3df' },
    circuitTrace:   { card:'#021614',  frame:'#3af0c8', chip:'#06241f', cta:'#3af0c8', ctaText:'#02110d' },
    paperCut:       { card:'#f6ecdc',  frame:'#d7c1a3', chip:'#fff7ec', cta:'#1f2933', ctaText:'#ffffff' },
    liquidChrome:   { card:'#d6e0e9',  frame:'#ffffff', chip:'#edf5fb', cta:'#c9d6e1', ctaText:'#111827' },
    pixelArcade:    { card:'#4a6dff',  frame:'#1c2153', chip:'#ffd447', cta:'#ffd447', ctaText:'#171b48' },
    win98:          { card:'#d4d0c8',  frame:'#808080', chip:'#efebe5', cta:'#d4d0c8', ctaText:'#0f172a' },
  }[p];

  if (fixed) {
    return {
      card:    card   || fixed.card,
      frame:   frame  || fixed.frame,
      chip:    chip   || fixed.chip,
      chipFr:  chipFr || fixed.frame,
      cta:     cta    || fixed.cta,
      ctaFr:   ctaFr  || 'transparent',
      ctaText: cta ? _ctaText(cta) : fixed.ctaText,
      input:   input  || fixed.card,
      inputFr: inputFr|| fixed.frame,
    };
  }

  // Dynamic computation from accent (covers softPill, glassOutline, solidBlock, sharpFrame, accentGlow, etc.)
  const isLight = _isLight(accent);
  const neutralBase = isLight ? '#ffffff' : '#0c1427';
  const accentCore = isLight ? _mix(accent, '#0f172a', 0.14) : accent;
  const cardMixes   = { glassOutline:0.88, solidBlock:0.52, sharpFrame:0.58, accentGlow:0.56, default:0.7 };
  const cardAlphas  = { glassOutline:0.035,solidBlock:0.22, sharpFrame:0.20, accentGlow:0.20, default:0.12 };
  const chipAlphas  = { glassOutline:0.04, solidBlock:0.26, sharpFrame:0.22, accentGlow:0.15, default:0.13 };
  const borderAlphs = { glassOutline:0.66, solidBlock:0.50, sharpFrame:0.72, accentGlow:0.74, default:0.52 };
  const cm = cardMixes[p]  || cardMixes.default;
  const ca = cardAlphas[p] || cardAlphas.default;
  const chA= chipAlphas[p] || chipAlphas.default;
  const bA = borderAlphs[p]|| borderAlphs.default;
  const cardBase = _mix(accentCore, neutralBase, cm);
  const chipBase = _mix(accentCore, neutralBase, 0.48);
  const borderBase = _mix(_isLight(accent)?_mix(accent,'#0f172a',0.42):_mix(accent,'#ffffff',0.08), accentCore, 0.28);
  const ctaFill = cta || accentCore;
  return {
    card:    card    || _rgba(cardBase, ca),
    frame:   frame   || _rgba(borderBase, bA),
    chip:    chip    || _rgba(chipBase, chA),
    chipFr:  chipFr  || _rgba(borderBase, bA),
    cta:     ctaFill,
    ctaFr:   ctaFr   || 'transparent',
    ctaText: _ctaText(ctaFill),
    input:   input   || _rgba(_mix(accentCore, neutralBase, 0.36), 0.10),
    inputFr: inputFr || _rgba(borderBase, bA),
  };
};

const TEXT_ROLE_BASE = {
  canvasName: { fontSize: 42, lineHeight: 46, fontWeight: '900' },
  canvasIntro: { fontSize: 13, lineHeight: 19, fontWeight: '600' },
  heroTitle: { fontSize: 22, lineHeight: 28, fontWeight: '900' },
  button: { fontSize: 14, lineHeight: 18, fontWeight: '900' },
  chipText: { fontSize: 12, lineHeight: 16, fontWeight: '700' },
  footer: { fontSize: 11, lineHeight: 16, fontWeight: '700' },
};

const TEXT_ROLE_OVERRIDES = {
  editorial: {
    canvasName: { fontSize: 40, lineHeight: 48, fontWeight: '700' },
    heroTitle: { fontSize: 23, lineHeight: 31, fontWeight: '800' },
    button: { lineHeight: 19, fontWeight: '800' },
    footer: { lineHeight: 17, fontWeight: '600' },
  },
  condensed: {
    canvasName: { fontSize: 44, lineHeight: 46, fontWeight: '900' },
    heroTitle: { fontSize: 24, lineHeight: 28, fontWeight: '900' },
    button: { fontSize: 13, lineHeight: 17, fontWeight: '900' },
    chipText: { lineHeight: 15, fontWeight: '800' },
    footer: { fontSize: 10, lineHeight: 15, fontWeight: '800' },
  },
  mono: {
    canvasName: { fontSize: 38, lineHeight: 44, fontWeight: '700' },
    heroTitle: { fontSize: 21, lineHeight: 27, fontWeight: '700' },
    button: { fontSize: 13, lineHeight: 17, fontWeight: '700' },
    footer: { fontSize: 10, lineHeight: 15, fontWeight: '600' },
  },
  soft: {
    canvasName: { lineHeight: 48, fontWeight: '800' },
    canvasIntro: { fontSize: 13, lineHeight: 21, fontWeight: '600' },
    heroTitle: { fontSize: 23, lineHeight: 30, fontWeight: '800' },
    button: { fontWeight: '800' },
    footer: { lineHeight: 17, fontWeight: '700' },
  },
  impact: {
    canvasName: { fontSize: 44, lineHeight: 46, fontWeight: '900' },
    canvasIntro: { lineHeight: 18, fontWeight: '700' },
    heroTitle: { fontSize: 25, lineHeight: 30, fontWeight: '900' },
    chipText: { lineHeight: 15, fontWeight: '800' },
    footer: { lineHeight: 15, fontWeight: '800' },
  },
};

const buildTextRoleVars = (theme, scale) => {
  const preset = theme.textStylePreset || 'clean';
  const overrides = TEXT_ROLE_OVERRIDES[preset] || {};
  return Object.entries(TEXT_ROLE_BASE).reduce((vars, [role, base]) => {
    const recipe = { ...base, ...(overrides[role] || {}) };
    const prefix = `--lb-text-${role}`;
    vars[`${prefix}-size`] = `${recipe.fontSize * scale}px`;
    vars[`${prefix}-line`] = `${recipe.lineHeight * scale}px`;
    vars[`${prefix}-weight`] = recipe.fontWeight;
    return vars;
  }, {});
};

const buildFontFamily = (theme) => {
  if (theme.textStylePreset === 'mono') return 'ui-monospace, SFMono-Regular, Menlo, monospace';
  if (theme.textStylePreset === 'editorial') return 'Georgia, "Times New Roman", serif';
  if (theme.textStylePreset === 'condensed') return '"Arial Narrow", "Roboto Condensed", Arial, sans-serif';
  if (theme.textStylePreset === 'impact') return '"Arial Narrow", Impact, "Roboto Condensed", Arial, sans-serif';
  if (theme.fontFamily === 'serif') return 'Georgia, "Times New Roman", serif';
  if (theme.fontFamily === 'monospace') return 'ui-monospace, SFMono-Regular, Menlo, monospace';
  if (theme.fontFamily === 'sans-serif-condensed') return '"Arial Narrow", "Roboto Condensed", Arial, sans-serif';
  if (theme.fontFamily && theme.fontFamily !== 'System' && theme.fontFamily !== 'Default') {
    return `"${theme.fontFamily}", "DM Sans", sans-serif`;
  }
  return '"DM Sans", ui-sans-serif, system-ui, sans-serif';
};

const buildBackground = (theme) => {
  const value = theme.backgroundValue || 'linear-gradient(145deg, #081225 0%, #0f3d53 45%, #f97316 100%)';
  const imageUrl = resolveRenderableImageRef(value);
  if (theme.backgroundType === 'image' && imageUrl) {
    return `linear-gradient(180deg, rgba(2,6,23,0.42), rgba(2,6,23,0.76)), url("${imageUrl}") center center / cover`;
  }
  return value;
};

const inferLinkMediaKind = (item) => {
  const value = `${item?.url || ''} ${item?.trackedPath || ''} ${item?.embedUrl || ''}`.toLowerCase();
  if (value.includes('youtu.be') || value.includes('youtube.com')) return 'youtube';
  if (value.includes('spotify.com')) return 'spotify';
  if (value.includes('twitter.com') || value.includes('x.com')) return 'tweet';
  if (isPhotoLinkItem(item)) return 'photo';
  return 'generic';
};

const buildPreviewGridLayout = (colSpan, rowSpan, justify = 'center', preferredCol, preferredRow) => ({
  span: colSpan === 4 ? 'full' : colSpan === 3 ? 'wide' : colSpan === 2 && rowSpan === 2 ? 'square' : 'half',
  align: justify === 'start' ? 'left' : justify === 'end' ? 'right' : 'center',
  colSpan,
  rowSpan,
  justify,
  ...(preferredCol !== undefined ? { preferredCol } : {}),
  ...(preferredRow !== undefined ? { preferredRow } : {}),
});

const defaultBlockLayout = (blockOrType) => {
  const type = typeof blockOrType === 'string' ? blockOrType : blockOrType?.type;
  switch (type) {
    case 'youtubeEmbed':
    case 'spotifyEmbed':
    case 'postEmbed':
      return buildPreviewGridLayout(4, 3, 'center');
    case 'announcement':
    case 'pulseCard':
    case 'emailCapture':
    case 'donation':
      return buildPreviewGridLayout(3, 2, 'center');
    case 'contactActions':
      return buildPreviewGridLayout(1, 1, 'center');
    case 'linkCollection': {
      const leadItem = Array.isArray(blockOrType?.items) ? blockOrType.items[0] : null;
      const mediaKind = leadItem ? inferLinkMediaKind(leadItem) : 'generic';
      if (mediaKind === 'youtube' || mediaKind === 'spotify' || mediaKind === 'tweet') {
        return buildPreviewGridLayout(4, 3, 'center');
      }
      return buildPreviewGridLayout(3, 2, 'center');
    }
    default:
      return buildPreviewGridLayout(4, 2, 'center');
  }
};

const defaultBlockShapePreset = (type) => {
  switch (type) {
    case 'socialStrip':
    case 'contactActions':
      return 'pill';
    case 'youtubeEmbed':
    case 'spotifyEmbed':
    case 'postEmbed':
      return 'landscape';
    case 'linkCollection':
      return 'softRect';
    default:
      return 'roundedSquare';
  }
};

const normalizeShapeForLayout = (shapePreset, layout) => {
  const normalized = shapePreset === 'square' ? 'roundedSquare' : (shapePreset || 'roundedSquare');
  return layout?.colSpan === 1 && layout?.rowSpan === 1 && normalized === 'pill' ? 'roundedSquare' : normalized;
};

const resolveBlockLayout = (block) => {
  const fallback = defaultBlockLayout(block);
  const layout = block.layout || {};
  if (layout.colSpan && layout.rowSpan) {
    return buildPreviewGridLayout(
      Math.min(4, Math.max(1, Number(layout.colSpan))),
      Math.min(3, Math.max(1, Number(layout.rowSpan))),
      layout.justify || (layout.align === 'left' ? 'start' : layout.align === 'right' ? 'end' : 'center'),
      Number.isFinite(Number(layout.preferredCol))
        ? Math.max(0, Math.min(3, Math.round(Number(layout.preferredCol))))
        : undefined,
      Number.isFinite(Number(layout.preferredRow)) ? Math.max(0, Math.round(Number(layout.preferredRow))) : undefined,
    );
  }
  if (layout.span) {
    const justify = layout.align === 'left' ? 'start' : layout.align === 'right' ? 'end' : 'center';
    if (layout.span === 'full') return buildPreviewGridLayout(4, 2, justify);
    if (layout.span === 'wide') return buildPreviewGridLayout(3, 2, justify);
    if (layout.span === 'square') return buildPreviewGridLayout(2, 2, justify);
    return buildPreviewGridLayout(2, 1, justify);
  }
  return {
    ...fallback,
    ...layout,
    colSpan: Math.min(4, Math.max(1, Number(layout.colSpan || fallback.colSpan))),
    rowSpan: Math.min(3, Math.max(1, Number(layout.rowSpan || fallback.rowSpan))),
    justify: layout.justify || fallback.justify,
    align: layout.align || fallback.align,
  };
};

const isStudioTarget = (value) => {
  if (!value || typeof value !== 'object') return false;
  if (value.kind === 'hero' || value.kind === 'block') return typeof value.blockId === 'string';
  if (value.kind === 'linkItem' || value.kind === 'socialItem') {
    return typeof value.blockId === 'string' && typeof value.itemId === 'string';
  }
  return false;
};

const isTargetSelected = (selectedTarget, kind, blockId, itemId) => {
  if (!selectedTarget || selectedTarget.kind !== kind || selectedTarget.blockId !== blockId) return false;
  if (itemId === undefined) return !selectedTarget.itemId;
  return selectedTarget.itemId === itemId;
};

const getCandidateColumns = (columns, colSpan, justify, preferredCol) => {
  const maxStart = Math.max(0, columns - colSpan);
  const base = Array.from({ length: maxStart + 1 }, (_, index) => index);
  if (preferredCol !== undefined) {
    const target = Math.max(0, Math.min(preferredCol, maxStart));
    return [...base].sort((left, right) => Math.abs(left - target) - Math.abs(right - target));
  }
  if (justify === 'start') return base;
  if (justify === 'end') return [...base].reverse();
  return [...base].sort((left, right) => {
    const center = (columns - colSpan) / 2;
    return Math.abs(left - center) - Math.abs(right - center);
  });
};

const getCandidateRows = (preferredRow, fallbackLimit) => {
  if (preferredRow === undefined) {
    return Array.from({ length: fallbackLimit + 1 }, (_, index) => index);
  }
  const target = Math.max(0, Math.round(preferredRow));
  const candidates = [target];
  for (let distance = 1; distance <= fallbackLimit + 4; distance += 1) {
    const before = target - distance;
    const after = target + distance;
    if (before >= 0) candidates.push(before);
    candidates.push(after);
  }
  return candidates;
};

const packPuzzleFrames = (
  items,
  containerWidth,
  resolveLayout,
  columnGap = PUZZLE_GRID_COLUMN_GAP,
  rowGap = PUZZLE_GRID_ROW_GAP,
  columns = PUZZLE_GRID_COLUMNS,
  options,
) => {
  if (containerWidth <= 0) return { frames: [], totalHeight: 0 };
  const cellSize = (containerWidth - columnGap * (columns - 1)) / columns;
  const occupancy = [];
  const ensureRow = (rowIndex) => {
    while (!occupancy[rowIndex]) {
      occupancy.push(Array.from({ length: columns }, () => false));
    }
  };
  const fitsAt = (rowIndex, colIndex, colSpan, rowSpan) => {
    for (let row = rowIndex; row < rowIndex + rowSpan; row += 1) {
      ensureRow(row);
      for (let col = colIndex; col < colIndex + colSpan; col += 1) {
        if (col >= columns || occupancy[row][col]) return false;
      }
    }
    return true;
  };
  const markOccupied = (rowIndex, colIndex, colSpan, rowSpan) => {
    for (let row = rowIndex; row < rowIndex + rowSpan; row += 1) {
      ensureRow(row);
      for (let col = colIndex; col < colIndex + colSpan; col += 1) {
        occupancy[row][col] = true;
      }
    }
  };

  const entries = items.map((item, index) => ({ item, index, layout: resolveLayout(item) }));
  const pinnedEntry = options?.pinnedBlockId ? entries.find((entry) => entry.item.id === options.pinnedBlockId) : undefined;
  const hasExplicitSlot = (layout) => layout.preferredCol !== undefined || layout.preferredRow !== undefined;
  const placementOrder = [
    ...(pinnedEntry ? [pinnedEntry] : []),
    ...entries.filter((entry) => entry !== pinnedEntry && hasExplicitSlot(entry.layout)),
    ...entries.filter((entry) => entry !== pinnedEntry && !hasExplicitSlot(entry.layout)),
  ];

  const frames = placementOrder.map(({ item, index, layout }) => {
    const pinnedFrame = item.id === options?.pinnedBlockId ? options.pinnedPreferredFrame : undefined;
    const forcedCol = pinnedFrame
      ? Math.max(0, Math.min(Math.round(pinnedFrame.col), Math.max(0, columns - layout.colSpan)))
      : undefined;
    const forcedRow = pinnedFrame ? Math.max(0, Math.round(pinnedFrame.row)) : undefined;
    const rowCandidates = forcedRow !== undefined
      ? [forcedRow]
      : getCandidateRows(layout.preferredRow, occupancy.length + items.length + 8);
    let rowIndex = rowCandidates[0] || 0;
    let placedCol = 0;
    let candidateIndex = 0;
    while (true) {
      rowIndex = rowCandidates[candidateIndex] ?? candidateIndex;
      const candidates = forcedCol !== undefined
        ? [forcedCol]
        : getCandidateColumns(columns, layout.colSpan, layout.justify, layout.preferredCol);
      const nextCol = candidates.find((candidate) => fitsAt(rowIndex, candidate, layout.colSpan, layout.rowSpan));
      if (nextCol !== undefined) {
        placedCol = nextCol;
        break;
      }
      candidateIndex += 1;
    }
    markOccupied(rowIndex, placedCol, layout.colSpan, layout.rowSpan);
    const isCenteredWide = layout.colSpan === 3 && layout.rowSpan === 1 && columns === 4 && layout.preferredCol === undefined && placedCol === 0;
    return {
      id: item.id,
      index,
      col: placedCol,
      row: rowIndex,
      colSpan: layout.colSpan,
      rowSpan: layout.rowSpan,
      x: placedCol * (cellSize + columnGap) + (isCenteredWide ? (cellSize + columnGap) / 2 : 0),
      y: rowIndex * (cellSize + rowGap),
      width: layout.colSpan * cellSize + (layout.colSpan - 1) * columnGap,
      height: layout.rowSpan * cellSize + (layout.rowSpan - 1) * rowGap,
    };
  }).sort((left, right) => left.index - right.index);

  const maxBottom = frames.reduce((highest, frame) => Math.max(highest, frame.y + frame.height), 0);
  return { frames, totalHeight: maxBottom };
};

const adaptiveDropTileOptions = [
  { colSpan: 4, rowSpan: 2 },
  { colSpan: 3, rowSpan: 2 },
  { colSpan: 2, rowSpan: 2 },
  { colSpan: 2, rowSpan: 1 },
  { colSpan: 1, rowSpan: 3 },
  { colSpan: 1, rowSpan: 2 },
  { colSpan: 1, rowSpan: 1 },
];

const findAdaptiveDropLayout = (frames, activeId, targetCol, targetRow, currentLayout, columns = PUZZLE_GRID_COLUMNS) => {
  const occupied = new Set();
  frames.forEach((frame) => {
    if (frame.id === activeId) return;
    for (let row = frame.row; row < frame.row + frame.rowSpan; row += 1) {
      for (let col = frame.col; col < frame.col + frame.colSpan; col += 1) {
        occupied.add(`${row}:${col}`);
      }
    }
  });

  const startRow = Math.max(0, targetRow);
  const anchorCol = Math.max(0, Math.min(targetCol, columns - 1));
  const fitsAt = (colSpan, rowSpan, colStart, rowStart) => {
    if (colStart + colSpan > columns) return false;
    for (let row = rowStart; row < rowStart + rowSpan; row += 1) {
      for (let col = colStart; col < colStart + colSpan; col += 1) {
        if (occupied.has(`${row}:${col}`)) return false;
      }
    }
    return true;
  };
  const candidateStartsFor = (colSpan) => {
    const starts = Array.from({ length: Math.max(0, columns - colSpan) + 1 }, (_, index) => index)
      .filter((colStart) => colStart <= anchorCol && anchorCol < colStart + colSpan);
    return starts.sort((left, right) => {
      const leftCenter = left + colSpan / 2;
      const rightCenter = right + colSpan / 2;
      return Math.abs(anchorCol + 0.5 - leftCenter) - Math.abs(anchorCol + 0.5 - rightCenter);
    });
  };

  const seen = new Set();
  const currentArea = currentLayout.colSpan * currentLayout.rowSpan;
  const dropOptions = [
    { colSpan: currentLayout.colSpan, rowSpan: currentLayout.rowSpan },
    ...adaptiveDropTileOptions,
  ].filter((option) => {
    const key = `${option.colSpan}x${option.rowSpan}`;
    if (seen.has(key)) return false;
    if (option.colSpan * option.rowSpan > currentArea) return false;
    seen.add(key);
    return true;
  });

  const candidates = dropOptions
    .flatMap((option) =>
      candidateStartsFor(option.colSpan)
        .filter((colStart) => fitsAt(option.colSpan, option.rowSpan, colStart, startRow))
        .map((colStart) => ({
          ...option,
          preferredCol: colStart,
          area: option.colSpan * option.rowSpan,
          isCurrent: option.colSpan === currentLayout.colSpan && option.rowSpan === currentLayout.rowSpan,
          footprintDistance: Math.abs(option.colSpan - currentLayout.colSpan) + Math.abs(option.rowSpan - currentLayout.rowSpan),
          centerDistance: Math.abs(anchorCol + 0.5 - (colStart + option.colSpan / 2)),
        })),
    )
    .sort((left, right) => {
      if (left.isCurrent !== right.isCurrent) return left.isCurrent ? -1 : 1;
      if (right.area !== left.area) return right.area - left.area;
      if (left.footprintDistance !== right.footprintDistance) return left.footprintDistance - right.footprintDistance;
      return left.centerDistance - right.centerDistance;
    });

  return candidates[0] || { colSpan: 1, rowSpan: 1, preferredCol: anchorCol };
};

const getBlockSectionProps = (block, frame, selectedTarget) => {
  const layout = resolveBlockLayout(block);
  const frameLayout = frame || layout;
  const shape = normalizeShapeForLayout(block.shapePreset || defaultBlockShapePreset(block.type), frameLayout);
  const preferredCol = Number.isFinite(Number(frameLayout.preferredCol))
    ? Math.max(0, Math.min(4 - frameLayout.colSpan, Math.round(Number(frameLayout.preferredCol))))
    : null;
  const preferredRow = Number.isFinite(Number(frameLayout.preferredRow))
    ? Math.max(0, Math.round(Number(frameLayout.preferredRow)))
    : null;
  const selected = isTargetSelected(selectedTarget, 'block', block.id);
  return {
    'data-lb-kind': 'block',
    'data-lb-block-id': block.id,
    'data-lb-selected': selected ? 'true' : undefined,
    className: [
      'linkboard-panel',
      'linkboard-section',
      `linkboard-block-${block.type}`,
      `linkboard-shape-${shape}`,
      `linkboard-size-${frameLayout.colSpan}x${frameLayout.rowSpan}`,
      `linkboard-align-${frameLayout.align || layout.align}`,
      block.emphasis ? `linkboard-emphasis-${block.emphasis}` : '',
    ].filter(Boolean).join(' '),
    style: frame ? {
      '--lb-col-span': frame.colSpan,
      '--lb-row-span': frame.rowSpan,
      left: frame.x,
      top: frame.y,
      width: frame.width,
      height: frame.height,
      minHeight: 0,
      maxHeight: frame.height,
    } : {
      '--lb-col-span': frameLayout.colSpan,
      '--lb-row-span': frameLayout.rowSpan,
      '--lb-preferred-col': preferredCol ?? 0,
      '--lb-preferred-row': preferredRow ?? 0,
    },
  };
};

const resolveMicrositeRequest = (locationLike) => {
  if (!locationLike) return null;
  const pathname = locationLike.pathname || '/';
  const hostname = (locationLike.hostname || '').toLowerCase();
  const searchParams = new URLSearchParams(locationLike.search || '');
  const isKnownAppHost = APP_HOSTS.includes(hostname);

  if (pathname === '/studio-preview') {
    return {
      handle: undefined,
      qrToken: undefined,
      host: undefined,
      mode: 'studio-preview',
    };
  }

  if (pathname.startsWith('/@')) {
    return {
      handle: pathname.slice(2),
      qrToken: searchParams.get('qrToken') || undefined,
      host: undefined,
      mode: 'handle',
    };
  }

  if (pathname.startsWith('/q/')) {
    return {
      qrToken: pathname.split('/')[2],
      handle: undefined,
      host: undefined,
      mode: 'qr',
    };
  }

  if (pathname === '/' && !isKnownAppHost) {
    return {
      handle: undefined,
      qrToken: undefined,
      host: locationLike.host,
      mode: 'domain',
    };
  }

  return null;
};

export const shouldRenderPublicMicrosite = (locationLike) => Boolean(resolveMicrositeRequest(locationLike));

const PublicMicrositeApp = ({ initialRequest }) => {
  const request = useMemo(
    () => initialRequest || resolveMicrositeRequest(window.location),
    [initialRequest]
  );
  const isStudioPreview = request?.mode === 'studio-preview';
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePreset, setActivePreset] = useState('stack');
  const [leadValue, setLeadValue] = useState('');
  const [leadState, setLeadState] = useState({ saving: false, message: '' });
  const [donationState, setDonationState] = useState({ saving: false, message: '' });
  const [pulse, setPulse] = useState(null);
  const [creative, setCreative] = useState(null);
  const [creativeViewed, setCreativeViewed] = useState(false);
  const [studioRuntimeState, setStudioRuntimeState] = useState({
    editorMode: false,
    selectedTarget: null,
    focusArea: 'content',
    surfaceSetupBlockId: null,
    trashBottomOffset: 88,
    forcedPreset: null,
  });
  const blockGridRef = useRef(null);
  const dragStateRef = useRef(null);
  const holdTimerRef = useRef(null);
  const [boardWidth, setBoardWidth] = useState(0);

  useEffect(() => {
    if (isStudioPreview) {
      setLoading(false);
      setError('');
      return undefined;
    }
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const nextPayload = await resolveMicrosite(request || {});
        if (!active) return;
        assertSupportedPublicDocument(nextPayload?.document);
        setPayload(nextPayload);
        setActivePreset(normalizePublicPreset(nextPayload?.document?.display?.defaultPreset || 'stack'));
      } catch (nextError) {
        if (!active) return;
        setError(nextError.message || 'Microsite could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [isStudioPreview, request]);

  useEffect(() => {
    if (!isStudioPreview) return undefined;
    const postReady = () => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: 'ready',
        runtimeHash: LINKBOARD_RUNTIME_HASH,
      }));
    };
    const applyRuntimeState = (message) => {
      setStudioRuntimeState((previous) => ({
        ...previous,
        editorMode: message.editorMode === true,
        selectedTarget: isStudioTarget(message.selectedTarget) ? message.selectedTarget : null,
        focusArea: message.focusArea || previous.focusArea,
        surfaceSetupBlockId: typeof message.surfaceSetupBlockId === 'string' ? message.surfaceSetupBlockId : null,
        trashBottomOffset: Number.isFinite(Number(message.trashBottomOffset))
          ? Math.max(0, Number(message.trashBottomOffset))
          : previous.trashBottomOffset,
        forcedPreset: message.forcedPreset || null,
      }));
    };
    const applyDocument = (document, message = {}) => {
      if (!document?.schemaVersion) return;
      assertSupportedPublicDocument(document);
      setPayload({
        siteId: document.siteId,
        document,
        focusState: document.display?.focusMode || { enabled: false },
        adPolicy: { adsEnabled: false },
        sessionToken: null,
      });
      setActivePreset(normalizePublicPreset(message.forcedPreset || document.display?.defaultPreset || 'stack'));
      setError('');
      setLoading(false);
    };
    const handleMessage = (event) => {
      let message = event.data;
      if (typeof message === 'string') {
        try {
          message = JSON.parse(message);
        } catch (_error) {
          return;
        }
      }
      if (message?.type === 'linkboard:setDocument') {
        try {
          applyRuntimeState(message);
          applyDocument(message.document, message);
        } catch (nextError) {
          setError(nextError.message || 'Studio document could not be rendered.');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    window.document.addEventListener('message', handleMessage);
    postReady();
    return () => {
      window.removeEventListener('message', handleMessage);
      window.document.removeEventListener('message', handleMessage);
    };
  }, [isStudioPreview]);

  useEffect(() => {
    if (isStudioPreview || !payload?.document?.profile?.handle) return;
    let active = true;
    fetchPulseStatus(payload.document.profile.handle).then((status) => {
      if (active && status) {
        setPulse(status);
      }
    });
    return () => {
      active = false;
    };
  }, [isStudioPreview, payload?.document?.profile?.handle]);

  useEffect(() => {
    if (isStudioPreview || !payload?.siteId || !payload?.adPolicy?.adsEnabled) return;
    let active = true;
    fetchSponsorCreative({ slotKey: 'inline-primary', siteId: payload.siteId }).then((response) => {
      if (active) setCreative(response?.creative || null);
    });
    return () => {
      active = false;
    };
  }, [isStudioPreview, payload?.siteId, payload?.adPolicy?.adsEnabled]);

  useEffect(() => {
    if (isStudioPreview || !creative || creativeViewed || !payload?.sessionToken) return undefined;
    const timer = window.setTimeout(async () => {
      await confirmSponsorView({
        sessionToken: payload.sessionToken,
        slotKey: 'inline-primary',
        creativeId: creative.id,
      });
      await postMicrositeEvents({
        sessionToken: payload.sessionToken,
        events: [
          {
            eventType: 'ad_viewable',
            eventTime: new Date().toISOString(),
            adSlotId: 'inline-primary',
            meta: { creativeId: creative.id },
          },
        ],
      });
      setCreativeViewed(true);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [isStudioPreview, creative, creativeViewed, payload?.sessionToken]);

  useEffect(() => {
    if (isStudioPreview || !payload?.sessionToken || !payload?.siteId) return;
    postMicrositeEvents({
      sessionToken: payload.sessionToken,
      events: [
        {
          eventType: 'page_view',
          eventTime: new Date().toISOString(),
          meta: { mode: request?.mode || 'handle' },
        },
      ],
    });
  }, [isStudioPreview, payload?.sessionToken, payload?.siteId, request?.mode]);

  const document = payload?.document;

  // Tema değişkenlerini element üzerinde inline olarak set ediyoruz.
  // document.documentElement'e (root) setlemek CSS class override sorununa yol açar:
  // .linkboard-runtime { --lb-bg: ... } class tanımı, root'tan inherit edilen değeri ezer.
  // Inline style her zaman class selector'dan önce gelir — bu nedenle doğrudan element üzerinde uygulanıyor.
  const themeStyle = useMemo(() => {
    if (!document?.theme) return {};
    const theme = document.theme;
    const accent = theme.accentColor || '#f97316';
    const bg = buildBackground(theme);
    const font = buildFontFamily(theme);
    const scale = Number.isFinite(Number(theme.typographyScale))
      ? Math.max(0.82, Math.min(1.24, Number(theme.typographyScale)))
      : 1;
    // Surface palette — same logic as mobile buildPreviewSurfacePalette
    const sv = buildSurfaceVars(theme);
    const dynamic = theme.backgroundSettings?.dynamic || {};
    const grainient = theme.backgroundSettings?.grainient || {};
    const pixelSnow = theme.backgroundSettings?.pixelSnow || {};
    const iconDrift = theme.backgroundSettings?.iconDrift || {};
    const iconDriftImage = resolveRenderableImageRef(iconDrift.imageUri);
    const iconDriftSpeed = clampNumber(iconDrift.speed, 0.2, 5, 1);
    const iconDriftRawSize = Number(iconDrift.itemSize);
    const iconDriftSize = Number.isFinite(iconDriftRawSize)
      ? Math.max(18, Math.min(140, iconDriftRawSize <= 1 ? iconDriftRawSize * 720 : iconDriftRawSize))
      : 54;
    const dynamicSpeed = clampNumber(dynamic.speed, 0.2, 3, 1);
    const dynamicDensity = clampNumber(dynamic.density, 0.25, 2.4, 1);
    const dynamicIntensity = clampNumber(dynamic.intensity, 0.2, 2.4, 1);
    const pixelSnowSpeed = clampNumber(pixelSnow.speed, 0.1, 5, 1);
    const pixelSnowSize = clampNumber(pixelSnow.flakeSize, 0.005, 0.05, 0.01);
    return {
      '--lb-bg': bg,
      '--lb-accent': accent,
      '--lb-text': theme.contentTextColor || theme.textColor || '#f8fafc',
      '--lb-muted': theme.contentMutedTextColor || theme.mutedTextColor || '#cbd5e1',
      '--lb-dynamic-primary': dynamic.primaryColor || grainient.color1 || accent,
      '--lb-dynamic-secondary': dynamic.secondaryColor || grainient.color2 || theme.mutedTextColor || '#60a5fa',
      '--lb-dynamic-accent': dynamic.accentColor || grainient.color3 || accent,
      '--lb-effect-speed': dynamicSpeed,
      '--lb-effect-density': dynamicDensity,
      '--lb-effect-intensity': dynamicIntensity,
      '--lb-effect-opacity': Math.min(0.92, Math.max(0.42, 0.52 + dynamicIntensity * 0.18)),
      '--lb-storm-speed': `${Math.max(3.2, 6.4 / dynamicSpeed)}s`,
      '--lb-digital-rain-speed': `${Math.max(3.2, 8 / dynamicSpeed)}s`,
      '--lb-density-cell': `${Math.max(14, Math.round(30 / dynamicDensity))}px`,
      '--lb-grainient-speed': `${Math.max(8, 28 / clampNumber(grainient.timeSpeed, 0.05, 2, 0.25))}s`,
      '--lb-pixel-snow-color': pixelSnow.color || '#ffffff',
      '--lb-pixel-snow-size': `${Math.max(1, Math.round(pixelSnowSize * 180))}px`,
      '--lb-pixel-snow-speed': `${Math.max(5, 22 / pixelSnowSpeed)}s`,
      '--lb-card': sv.card,
      '--lb-card-border': sv.frame,
      '--lb-chip': sv.chip,
      '--lb-chip-border': sv.chipFr,
      '--lb-cta': sv.cta,
      '--lb-cta-border': sv.ctaFr,
      '--lb-cta-text': sv.ctaText,
      '--lb-input': sv.input,
      '--lb-input-border': sv.inputFr,
      '--lb-icon-drift-image': iconDriftImage ? `url("${iconDriftImage}")` : 'none',
      '--lb-icon-drift-size': `${iconDriftSize}px`,
      '--lb-icon-drift-speed': `${Math.max(7, 26 / iconDriftSpeed)}s`,
      '--lb-font': font,
      '--lb-weight': /^(400|500|600|700|800|900)$/.test(String(theme.textWeight || '')) ? theme.textWeight : '700',
      '--lb-scale': scale,
      ...buildTextRoleVars(theme, scale),
      fontFamily: font,
      fontStyle: theme.textItalic ? 'italic' : 'normal',
      background: bg,
      backgroundAttachment: 'fixed',
    };
  }, [document?.theme]);

  const runtimeClasses = useMemo(() => {
    const theme = document?.theme;
    return [
      'linkboard-runtime',
      `lb-preset-${activePreset || document?.display?.defaultPreset || 'stack'}`,
      isStudioPreview ? `lb-focus-${studioRuntimeState.focusArea || 'content'}` : '',
      theme ? `lb-button-${theme.buttonShape || 'pill'}` : '',
      theme ? `lb-card-${theme.cardStyle || 'glass'}` : '',
      theme?.buttonStylePreset ? `lb-surface-${_classToken(theme.buttonStylePreset)}` : '',
      theme?.textItalic ? 'lb-italic' : '',
      theme?.backgroundVariant && theme.backgroundVariant !== 'none'
        ? `lb-variant-${theme.backgroundVariant}` : '',
    ].filter(Boolean).join(' ');
  }, [activePreset, document?.theme, document?.display?.defaultPreset, isStudioPreview, studioRuntimeState.focusArea]);

  const orderedBlocks = useMemo(() => {
    if (!document?.blocks) return [];
    return document.blocks.filter((block) => block.visible !== false);
  }, [document?.blocks]);

  const boardBlocks = useMemo(
    () => orderedBlocks.filter((block) => block.type !== 'hero' && block.type !== 'socialStrip'),
    [orderedBlocks],
  );

  useEffect(() => {
    const node = blockGridRef.current;
    if (!node) return undefined;

    const syncWidth = () => {
      const nextWidth = node.getBoundingClientRect().width;
      if (Number.isFinite(nextWidth) && nextWidth > 0) {
        setBoardWidth((previous) => (Math.abs(previous - nextWidth) > 0.5 ? nextWidth : previous));
      }
    };

    syncWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncWidth);
      return () => window.removeEventListener('resize', syncWidth);
    }

    const observer = new ResizeObserver(syncWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [boardBlocks.length, document?.siteId]);

  const packedPuzzle = useMemo(
    () => packPuzzleFrames(boardBlocks, boardWidth, resolveBlockLayout),
    [boardBlocks, boardWidth],
  );

  const frameByBlockId = useMemo(() => {
    const map = new Map();
    packedPuzzle.frames.forEach((frame) => map.set(frame.id, frame));
    return map;
  }, [packedPuzzle.frames]);

  const puzzleFramePayload = useMemo(
    () => packedPuzzle.frames.map(({ id, col, row, colSpan, rowSpan, x, y, width, height, index }) => ({
      id,
      col,
      row,
      colSpan,
      rowSpan,
      x,
      y,
      width,
      height,
      index,
    })),
    [packedPuzzle.frames],
  );

  useEffect(() => {
    if (!isStudioPreview || !boardWidth) return;
    window.ReactNativeWebView?.postMessage(JSON.stringify({
      type: 'puzzleFramesChange',
      frames: puzzleFramePayload,
    }));
  }, [boardWidth, isStudioPreview, puzzleFramePayload]);

  const trackBlockClick = async ({ blockId, itemId, href, kind }) => {
    if (!payload?.sessionToken) return;
    await postMicrositeEvents({
      sessionToken: payload.sessionToken,
      events: [
        {
          eventType: 'block_click',
          blockId,
          eventTime: new Date().toISOString(),
          meta: { itemId, href, kind },
        },
      ],
    });
  };

  const handleLeadSubmit = async (block) => {
    const value = leadValue.trim();
    const captureType = block.captureType || 'email';
    if (!value) {
      setLeadState({ saving: false, message: 'This field is required.' });
      return;
    }

    try {
      setLeadState({ saving: true, message: '' });
      const leadPayload = { source: 'public_microsite' };
      const leadRequest = {
        sessionToken: payload.sessionToken,
        blockId: block.id,
        payload: leadPayload,
        consentVersion: 'v1',
      };
      if (captureType === 'phone') {
        leadRequest.phone = value;
      } else if (captureType === 'username') {
        leadRequest.payload = { ...leadPayload, username: value };
      } else {
        leadRequest.email = value;
      }
      await submitMicrositeLead({
        ...leadRequest,
      });
      await postMicrositeEvents({
        sessionToken: payload.sessionToken,
        events: [
          {
            eventType: 'form_submit',
            blockId: block.id,
            eventTime: new Date().toISOString(),
            meta: { captureType, emailDomain: captureType === 'email' ? value.split('@')[1] || 'unknown' : undefined },
          },
        ],
      });
      setLeadValue('');
      setLeadState({ saving: false, message: 'Subscribed. Lead captured successfully.' });
    } catch (nextError) {
      setLeadState({ saving: false, message: nextError.message || 'Lead could not be saved.' });
    }
  };

  const handleDonation = async (blockId, amount) => {
    try {
      setDonationState({ saving: true, message: '' });
      await postMicrositeEvents({
        sessionToken: payload.sessionToken,
        events: [
          {
            eventType: 'donation_open',
            blockId,
            eventTime: new Date().toISOString(),
            meta: { amount },
          },
        ],
      });
      const response = await createDonationSession({
        sessionToken: payload.sessionToken,
        amount,
        blockId,
      });
      setDonationState({ saving: false, message: amount ? `Checkout opened for $${amount}.` : 'Checkout opened.' });
      window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
    } catch (nextError) {
      setDonationState({ saving: false, message: nextError.message || 'Donation session could not be created.' });
    }
  };

  if (loading) {
    return <div className="linkboard-runtime linkboard-loading">Loading microsite...</div>;
  }

  if (error || !document) {
    return <div className="linkboard-runtime linkboard-error">{error || 'Microsite not found.'}</div>;
  }

  const heroBlock = document.blocks.find((block) => block.type === 'hero');
  const socialBlock = orderedBlocks.find((block) => block.type === 'socialStrip');
  const heroSocialItems = (socialBlock?.items || []).filter((item) => sanitizeLinkHref(item.url));
  const displayName = cleanDefaultPublicCopy(document.profile.displayName) || document.profile.handle || 'IDAPPZ';
  const heroTitle = cleanDefaultPublicCopy(heroBlock?.title);
  const profileHeadline = cleanDefaultPublicCopy(document.profile.headline);
  const heroHeadline = heroTitle && heroTitle !== displayName ? heroTitle : profileHeadline;
  const heroCopy = String(heroBlock?.subtitle || document.profile.bio || '').trim();
  const avatarValue = String(document.profile.avatarAssetId || '').trim();
  const avatarSrc = resolveRenderableImageRef(avatarValue) || null;
  const shouldRenderAvatar = Boolean(avatarSrc || !avatarValue);
  const heroCtaEnabled = heroBlock?.ctaEnabled ?? Boolean(heroBlock?.ctaUrl || heroBlock?.ctaLabel);
  const heroCtaExplicit = heroBlock?.ctaEnabled === true;
  const heroCtaLabel = String(heroBlock?.ctaLabel || '').trim();
  const heroCtaHref = sanitizeLinkHref(heroBlock?.ctaUrl);
  const shouldRenderHeroCta = Boolean(
    heroCtaEnabled &&
    heroCtaHref &&
    (heroCtaExplicit || !isPlaceholderHeroCta(heroCtaLabel, heroCtaHref))
  );
  const sponsorCtaHref = sanitizeLinkHref(creative?.ctaUrl);
  const activeFocusTarget = payload?.focusState?.enabled ? payload.focusState.targetLinkItemId : null;
  const selectedTarget = studioRuntimeState.selectedTarget;
  const surfaceSetupBlockId = studioRuntimeState.surfaceSetupBlockId;
  const getAnchorTargetProps = (href) => {
    if (isStudioPreview) return {};
    if (!href || href.startsWith('#')) return {};
    return { target: '_blank', rel: 'noreferrer' };
  };
  const getSelectionAttrs = (kind, blockId, itemId) => ({
    'data-lb-kind': kind,
    'data-lb-block-id': blockId,
    ...(itemId ? { 'data-lb-item-id': itemId } : {}),
    'data-lb-selected': isTargetSelected(selectedTarget, kind, blockId, itemId) ? 'true' : undefined,
  });
  const postStudioEvent = (event) => {
    if (!isStudioPreview) return;
    window.ReactNativeWebView?.postMessage(JSON.stringify(event));
  };
  const handleStudioClickCapture = (event) => {
    if (!isStudioPreview) return;
    const targetElement = event.target?.closest?.('[data-lb-kind]');
    if (!targetElement) return;
    const target = {
      kind: targetElement.dataset.lbKind,
      blockId: targetElement.dataset.lbBlockId,
    };
    if (targetElement.dataset.lbItemId) target.itemId = targetElement.dataset.lbItemId;
    event.preventDefault();
    event.stopPropagation();
    if (target.kind === 'hero') {
      postStudioEvent({ type: 'heroPress' });
    }
    if (target.kind === 'socialItem' && targetElement.closest?.('.linkboard-social-dock')) {
      postStudioEvent({ type: 'heroSocialPress' });
    }
    postStudioEvent({ type: 'selectTarget', target });
  };
  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };
  const updateSurfaceSetupBlock = (blockId) => {
    setStudioRuntimeState((previous) => ({ ...previous, surfaceSetupBlockId: blockId }));
  };
  const getDropTarget = (clientX, clientY) => {
    const grid = blockGridRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const cellSize = (rect.width - PUZZLE_GRID_COLUMN_GAP * (PUZZLE_GRID_COLUMNS - 1)) / PUZZLE_GRID_COLUMNS;
    const horizontalPitch = cellSize + PUZZLE_GRID_COLUMN_GAP;
    const verticalPitch = cellSize + PUZZLE_GRID_ROW_GAP;
    return {
      col: Math.max(0, Math.min(PUZZLE_GRID_COLUMNS - 1, Math.floor((clientX - rect.left) / horizontalPitch))),
      row: Math.max(0, Math.round((clientY - rect.top - cellSize / 2) / verticalPitch)),
    };
  };
  const handleStudioBlockPointerDown = (event, block, frame) => {
    if (!isStudioPreview || !frame || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const target = { kind: 'block', blockId: block.id };
    const startX = event.clientX;
    const startY = event.clientY;
    dragStateRef.current = {
      block,
      frame,
      pointerId: event.pointerId,
      startX,
      startY,
      hasDragged: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    clearHoldTimer();
    holdTimerRef.current = window.setTimeout(() => {
      postStudioEvent({ type: 'selectTarget', target });
      updateSurfaceSetupBlock(block.id);
    }, PUZZLE_DRAG_HOLD_MS);
  };
  const handleStudioBlockPointerMove = (event) => {
    const state = dragStateRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - state.startX, event.clientY - state.startY);
    if (distance > 6) clearHoldTimer();
    if (distance >= PUZZLE_DRAG_COMMIT_DISTANCE) {
      state.hasDragged = true;
      event.preventDefault();
    }
  };
  const handleStudioBlockPointerEnd = (event) => {
    const state = dragStateRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    clearHoldTimer();
    dragStateRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    const target = { kind: 'block', blockId: state.block.id };
    if (!state.hasDragged) {
      postStudioEvent({ type: 'selectTarget', target });
      return;
    }

    const dropY = event.clientY;
    if (studioRuntimeState.trashBottomOffset && dropY > window.innerHeight - studioRuntimeState.trashBottomOffset) {
      postStudioEvent({ type: 'trashDrop', target });
      return;
    }

    const dropTarget = getDropTarget(event.clientX, event.clientY);
    const currentLayout = resolveBlockLayout(state.block);
    if (!dropTarget) return;
    const adaptiveLayout = findAdaptiveDropLayout(
      packedPuzzle.frames,
      state.block.id,
      dropTarget.col,
      dropTarget.row,
      currentLayout,
      PUZZLE_GRID_COLUMNS,
    );
    postStudioEvent({
      type: 'updateBlockSurface',
      blockId: state.block.id,
      patch: {
        colSpan: adaptiveLayout.colSpan,
        rowSpan: adaptiveLayout.rowSpan,
        justify: currentLayout.justify,
        preferredCol: Math.max(0, Math.min(adaptiveLayout.preferredCol, PUZZLE_GRID_COLUMNS - adaptiveLayout.colSpan)),
        preferredRow: dropTarget.row,
      },
    });
  };
  const renderSurfacePopover = () => {
    if (!isStudioPreview || !surfaceSetupBlockId) return null;
    const frame = frameByBlockId.get(surfaceSetupBlockId);
    if (!frame) return null;
    const currentBlock = boardBlocks.find((block) => block.id === surfaceSetupBlockId);
    if (!currentBlock) return null;
    const options = [
      { id: '1x1', colSpan: 1, rowSpan: 1 },
      { id: '2x1', colSpan: 2, rowSpan: 1 },
      { id: '2x2', colSpan: 2, rowSpan: 2 },
      { id: '3x2', colSpan: 3, rowSpan: 2 },
      { id: '4x2', colSpan: 4, rowSpan: 2 },
      { id: '4x3', colSpan: 4, rowSpan: 3 },
    ];
    const popoverWidth = 236;
    const left = Math.max(0, Math.min(frame.x, Math.max(0, boardWidth - popoverWidth)));
    const top = Math.max(0, frame.y - 48);
    return (
      <div
        className="linkboard-surface-popover"
        style={{ left, top, width: popoverWidth }}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        {options.map((option) => {
          const active = frame.colSpan === option.colSpan && frame.rowSpan === option.rowSpan;
          return (
            <button
              key={option.id}
              type="button"
              className={active ? 'active' : ''}
              onClick={() => {
                postStudioEvent({
                  type: 'updateBlockSurface',
                  blockId: currentBlock.id,
                  patch: {
                    colSpan: option.colSpan,
                    rowSpan: option.rowSpan,
                    justify: resolveBlockLayout(currentBlock).justify,
                    preferredCol: Math.max(0, Math.min(frame.col, PUZZLE_GRID_COLUMNS - option.colSpan)),
                    preferredRow: frame.row,
                  },
                });
                postStudioEvent({ type: 'surfaceSetupHandled', blockId: currentBlock.id });
                setStudioRuntimeState((previous) => ({ ...previous, surfaceSetupBlockId: null }));
              }}
            >
              {option.id}
            </button>
          );
        })}
      </div>
    );
  };
  const renderStackLinkItem = (block, item, kind = 'link_stack') => {
    const href = getRenderableLinkHref(item) || '#';
    const photoItem = isPhotoLinkItem(item);
    const imageSrc = resolveRenderableImageRef(item.imageUrl);
    const itemTitle = item.title || block.title || 'Link';

    if (photoItem) {
      return (
        <a
          key={item.id}
          className={`linkboard-photo-link ${item.emphasis || 'default'}`}
          {...getSelectionAttrs('linkItem', block.id, item.id)}
          href={href}
          {...getAnchorTargetProps(href)}
          onClick={() => trackBlockClick({ blockId: block.id, itemId: item.id, href, kind })}
        >
          <span className="linkboard-photo-head">
            <span className="lb-link-icon"><ImageIcon size={14} /></span>
            <span className="linkboard-photo-copy">
              <span className="linkboard-link-title">{itemTitle}</span>
              {item.subtitle && <span className="linkboard-link-subtitle">{item.subtitle}</span>}
            </span>
            <ArrowRight className="linkboard-photo-arrow" size={18} />
          </span>
          <span className="linkboard-photo-media">
            {imageSrc ? (
              <img src={imageSrc} alt="" loading="lazy" />
            ) : (
              <span className="linkboard-photo-placeholder"><ImageIcon size={34} /></span>
            )}
          </span>
        </a>
      );
    }

    return (
      <a
        key={item.id}
        className={`linkboard-link ${item.emphasis || 'default'}`}
        {...getSelectionAttrs('linkItem', block.id, item.id)}
        href={href}
        {...getAnchorTargetProps(href)}
        onClick={() => trackBlockClick({ blockId: block.id, itemId: item.id, href, kind })}
      >
        {imageSrc ? (
          <img className="lb-link-img" src={imageSrc} alt="" loading="lazy" />
        ) : item.icon ? (
          <span className="lb-link-icon">{item.icon.slice(0, 2).toUpperCase()}</span>
        ) : null}
        <div className="linkboard-link-main">
          <span className="linkboard-link-title">{itemTitle}</span>
          {item.subtitle && <span className="linkboard-link-subtitle">{item.subtitle}</span>}
        </div>
        <ArrowRight size={18} />
      </a>
    );
  };

  const renderCardImage = (item, className = 'linkboard-card-image') => {
    const imageSrc = resolveRenderableImageRef(item?.imageUrl);
    if (!imageSrc) return null;
    return (
      <span className={className}>
        <img src={imageSrc} alt="" loading="lazy" />
      </span>
    );
  };

  return (
    <div className={`${runtimeClasses}${isStudioPreview ? ' lb-mode-studio' : ''}`} style={themeStyle} onClickCapture={handleStudioClickCapture}>
      <main className="linkboard-shell">
        <section
          className="linkboard-panel linkboard-hero linkboard-hero-centered linkboard-orbit"
          {...getSelectionAttrs('hero', heroBlock?.id || 'hero-main')}
        >
          <div className="linkboard-hero-top">
            <span className="linkboard-eyebrow linkboard-handle-chip">
              @{document.profile.handle}
            </span>
          </div>
          {shouldRenderAvatar && (
            <div className="linkboard-avatar-wrap">
              {avatarSrc ? (
                <img
                  className="linkboard-avatar linkboard-avatar-img"
                  src={avatarSrc}
                  alt={displayName}
                  loading="lazy"
                />
              ) : (
                <div className="linkboard-avatar">{getInitials(displayName)}</div>
              )}
            </div>
          )}
          <h1 className="linkboard-title">{displayName}</h1>
          {heroHeadline && (
            <p className="linkboard-display" style={{ marginBottom: '0.4rem' }}>{heroHeadline}</p>
          )}
          {heroCopy && <p className="linkboard-copy">{heroCopy}</p>}
          {shouldRenderHeroCta && (
            <div className="linkboard-row linkboard-hero-cta-row">
              <a
                className="linkboard-pill active"
                href={heroCtaHref}
                {...getAnchorTargetProps(heroCtaHref)}
                onClick={() => trackBlockClick({ blockId: heroBlock?.id, href: heroCtaHref, kind: 'hero_cta' })}
              >
                <span>{heroCtaLabel || 'Open'}</span>
                <ArrowRight size={18} />
              </a>
            </div>
          )}
          {heroSocialItems.length > 0 && (
            <div className="linkboard-social-dock" aria-label="Social links">
              {heroSocialItems.map((item) => {
                const meta = getSocialMeta(item.network);
                const href = sanitizeLinkHref(item.url);
                return (
                  <a
                    key={item.id}
                    className="linkboard-social-dot"
                    {...getSelectionAttrs('socialItem', socialBlock.id, item.id)}
                    href={href}
                    {...getAnchorTargetProps(href)}
                    style={{ '--social-color': meta.color }}
                    aria-label={meta.label}
                    title={meta.label}
                    onClick={() => trackBlockClick({ blockId: socialBlock.id, itemId: item.id, href, kind: `social_${item.network}` })}
                  >
                    <span>{meta.short}</span>
                  </a>
                );
              })}
            </div>
          )}
        </section>

        {creative && sponsorCtaHref && (
          <section className="linkboard-panel linkboard-section linkboard-banner">
            <div>
              <small>Sponsored</small>
              <h2 className="linkboard-h2" style={{ marginTop: '0.25rem' }}>{creative.title}</h2>
              <p className="linkboard-copy">{creative.body}</p>
              <a
                className="linkboard-pill active"
                style={{ display: 'inline-flex', marginTop: '0.85rem' }}
                href={sponsorCtaHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackBlockClick({ blockId: 'ad-inline-primary', href: sponsorCtaHref, kind: 'ad_click' })}
              >
                {creative.ctaLabel} <ExternalLink size={15} />
              </a>
            </div>
            <img src={creative.imageUrl} alt={creative.title} />
          </section>
        )}

        <div
          ref={blockGridRef}
          className="linkboard-block-grid"
          style={{ height: boardWidth ? (packedPuzzle.totalHeight || 12) + PUZZLE_GRID_BOTTOM_BREATH : undefined }}
        >
        {boardBlocks.map((block) => {
          const frame = frameByBlockId.get(block.id);
          const sectionProps = {
            ...getBlockSectionProps(block, frame, selectedTarget),
            onPointerDown: (event) => handleStudioBlockPointerDown(event, block, frame),
            onPointerMove: handleStudioBlockPointerMove,
            onPointerUp: handleStudioBlockPointerEnd,
            onPointerCancel: handleStudioBlockPointerEnd,
          };

          if (block.type === 'announcement') {
            return (
              <section key={block.id} {...sectionProps}>
                <p className="linkboard-eyebrow">{block.label}</p>
                <h2 className="linkboard-h2" style={{ marginTop: '0.75rem' }}>{block.message}</h2>
              </section>
            );
          }

          if (block.type === 'linkCollection') {
            const rawItems = activeFocusTarget
              ? block.items.filter((item) => item.id === activeFocusTarget)
              : block.items.filter((item) => !item.suppressed);
            const items = rawItems.filter((item) => getRenderableLinkHref(item));
            if (!items.length) return null;
            const blockPreset = normalizePublicPreset(activePreset || document.display.defaultPreset || block.defaultPreset || 'stack');
            const photoDominant = items.length === 1 && isPhotoLinkItem(items[0]);
            return (
              <section key={block.id} {...sectionProps}>
                {block.title && !photoDominant ? (
                  <div className="linkboard-section-header">
                    <h2 className="linkboard-h2">{block.title}</h2>
                  </div>
                ) : null}
                {(blockPreset === 'grid' || blockPreset === 'masonry') && (
                  <div className={`linkboard-grid ${blockPreset === 'masonry' ? 'masonry' : ''}`}>
                    {items.map((item) => {
                      const href = getRenderableLinkHref(item);
                      return (
                        <a
                          key={item.id}
                          className="linkboard-grid-item"
                          {...getSelectionAttrs('linkItem', block.id, item.id)}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => trackBlockClick({ blockId: block.id, itemId: item.id, href, kind: 'link_grid' })}
                        >
                          {renderCardImage(item, 'linkboard-grid-image')}
                          <span className="linkboard-link-title">{item.title}</span>
                          <span>{item.subtitle || 'Open'}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
                {blockPreset === 'spotlight' && (
                  <div className="linkboard-spotlight">
                    {items[0] && (
                      <a
                        className={`linkboard-card featured ${items[0].emphasis || 'default'}`}
                        {...getSelectionAttrs('linkItem', block.id, items[0].id)}
                        href={getRenderableLinkHref(items[0])}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => trackBlockClick({ blockId: block.id, itemId: items[0].id, href: getRenderableLinkHref(items[0]), kind: 'link_spotlight' })}
                      >
                        {renderCardImage(items[0])}
                        <h3 className="linkboard-h2">{items[0].title}</h3>
                        <p className="linkboard-copy">{items[0].subtitle || 'Continue to destination'}</p>
                      </a>
                    )}
                    <div className="linkboard-link-list compact">
                      {items.slice(1).map((item) => {
                        const href = getRenderableLinkHref(item);
                        return (
                          <a
                            key={item.id}
                            className={`linkboard-link ${item.emphasis || 'default'}`}
                            {...getSelectionAttrs('linkItem', block.id, item.id)}
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => trackBlockClick({ blockId: block.id, itemId: item.id, href, kind: 'link_spotlight_secondary' })}
                          >
                            {renderCardImage(item, 'linkboard-compact-image')}
                            <div className="linkboard-link-main">
                              <span className="linkboard-link-title">{item.title}</span>
                              {item.subtitle && <span className="linkboard-link-subtitle">{item.subtitle}</span>}
                            </div>
                            <ArrowRight size={18} />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                {blockPreset === 'swipe' && (
                  <div className="linkboard-swipe">
                    {items.map((item) => (
                      <a
                        key={item.id}
                        className="linkboard-card"
                        {...getSelectionAttrs('linkItem', block.id, item.id)}
                        href={getRenderableLinkHref(item)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => trackBlockClick({ blockId: block.id, itemId: item.id, href: getRenderableLinkHref(item), kind: 'link_swipe' })}
                      >
                        {renderCardImage(item)}
                        <p className="linkboard-subtle">Tap.bio style card</p>
                        <h3 className="linkboard-h2">{item.title}</h3>
                        <p className="linkboard-copy">{item.subtitle || 'Continue to destination'}</p>
                      </a>
                    ))}
                  </div>
                )}
                {(blockPreset === 'stack' || blockPreset === 'timeline') && (
                  <div className={`linkboard-link-list ${blockPreset === 'timeline' ? 'timeline' : ''} ${photoDominant ? 'photo-dominant' : ''}`}>
                    {items.map((item) => renderStackLinkItem(block, item))}
                  </div>
                )}
              </section>
            );
          }

          if (block.type === 'socialStrip') {
            return null;
          }

          if (block.type === 'pulseCard') {
            return (
              <section key={block.id} {...sectionProps}>
                <p className="linkboard-eyebrow">Pulse Card</p>
                <h2 className="linkboard-h2" style={{ marginTop: '0.75rem' }}>{block.title}</h2>
                <p className="linkboard-copy">{pulse?.headline || block.fallbackText}</p>
                <p className="linkboard-toast">Updated {pulse?.updatedAt ? new Date(pulse.updatedAt).toLocaleString() : 'recently'}</p>
              </section>
            );
          }

          if (block.type === 'contactActions') {
            const actions = block.actions.filter((action) => buildActionHref(action));
            if (!actions.length) return null;
            return (
              <section key={block.id} {...sectionProps}>
                <h2 className="linkboard-h2">{block.title || 'Quick contact'}</h2>
                <div className="linkboard-actions">
                  {actions.map((action) => (
                    <a
                      key={action.id}
                      className="linkboard-action"
                      href={buildActionHref(action)}
                      target={action.actionType === 'email' || action.actionType === 'phone' ? undefined : '_blank'}
                      rel="noreferrer"
                      onClick={() => trackBlockClick({ blockId: block.id, itemId: action.id, href: buildActionHref(action), kind: action.actionType })}
                    >
                      {action.actionType === 'email' ? <Mail size={16} /> : <MessageCircle size={16} />} {action.label}
                    </a>
                  ))}
                </div>
              </section>
            );
          }

          if (block.type === 'emailCapture') {
            const inputConfig = getLeadInputConfig(block.captureType);
            return (
              <section key={block.id} {...sectionProps}>
                <h2 className="linkboard-h2">{block.title}</h2>
                {block.subtitle && <p className="linkboard-copy">{block.subtitle}</p>}
                <div className="linkboard-form">
                  <input
                    className="linkboard-input"
                    type={inputConfig.type}
                    placeholder={inputConfig.placeholder}
                    value={leadValue}
                    onChange={(event) => setLeadValue(event.target.value)}
                  />
                  <label className="linkboard-subtle">{block.consentText}</label>
                  <button className="linkboard-button" type="button" disabled={leadState.saving} onClick={() => handleLeadSubmit(block)}>
                    {leadState.saving ? 'Saving...' : block.buttonLabel}
                  </button>
                </div>
                {leadState.message && <p className="linkboard-toast">{leadState.message}</p>}
              </section>
            );
          }

          if (block.type === 'donation') {
            return (
              <section key={block.id} {...sectionProps}>
                <h2 className="linkboard-h2">{block.title}</h2>
                {block.subtitle && <p className="linkboard-copy">{block.subtitle}</p>}
                <div className="linkboard-row" style={{ marginTop: '0.9rem' }}>
                  {block.suggestedAmounts.map((amount) => (
                    <button key={amount} type="button" className="linkboard-pill" onClick={() => handleDonation(block.id, amount)}>
                      <CircleDollarSign size={16} /> ${amount}
                    </button>
                  ))}
                  {block.allowCustomAmount && (
                    <button type="button" className="linkboard-pill active" onClick={() => handleDonation(block.id, null)}>
                      Custom amount
                    </button>
                  )}
                </div>
                {donationState.message && <p className="linkboard-toast">{donationState.message}</p>}
              </section>
            );
          }

          if (block.type === 'youtubeEmbed' || block.type === 'spotifyEmbed' || block.type === 'postEmbed') {
            if (!block.embedUrl?.trim()) return null;
            const embedSrc = sanitizeEmbedUrl(block.embedUrl);
            const originalEmbedHref = sanitizeLinkHref(block.embedUrl);
            if (!embedSrc && block.type === 'postEmbed' && originalEmbedHref) {
              return (
                <section key={block.id} {...sectionProps}>
                  <h2 className="linkboard-h2">{block.title || 'Featured post'}</h2>
                  <a
                    className="linkboard-link"
                    href={originalEmbedHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackBlockClick({ blockId: block.id, href: originalEmbedHref, kind: 'post_embed' })}
                  >
                    <span className="linkboard-link-title">Open post</span>
                    <ExternalLink size={18} />
                  </a>
                </section>
              );
            }
            if (!embedSrc) return null;
            return (
              <section key={block.id} {...sectionProps}>
                <div className="linkboard-row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h2 className="linkboard-h2">{block.title || (block.type === 'youtubeEmbed' ? 'Video' : 'Audio')}</h2>
                    <p className="linkboard-subtle">Embedded from an allowlisted provider.</p>
                  </div>
                  {block.type === 'youtubeEmbed' ? <Play size={18} /> : <ExternalLink size={18} />}
                </div>
                <iframe
                  className="linkboard-embed"
                  src={embedSrc}
                  title={block.title || block.id}
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </section>
            );
          }

          if (block.type === 'richText') {
            return (
              <section key={block.id} {...sectionProps}>
                {block.title && <h2 className="linkboard-h2">{block.title}</h2>}
                <p className="linkboard-richtext">{block.content}</p>
              </section>
            );
          }

          if (block.type === 'divider') {
            return (
              <section key={block.id} {...sectionProps}>
                <hr className="linkboard-divider" />
              </section>
            );
          }

          if (block.type === 'spacer') {
            return (
              <section key={block.id} {...sectionProps}>
                <span style={{ height: block.size === 'lg' ? '2rem' : block.size === 'md' ? '1.25rem' : '0.75rem' }} />
              </section>
            );
          }

          return null;
        })}
        {renderSurfacePopover()}
        </div>

        {document.branding.footerMode !== 'none' && (
          <footer className="linkboard-footer">
            {document.branding.whiteLabel ? 'White-label runtime active' : 'Powered by IDAPPZ LinkBoard'}
          </footer>
        )}
      </main>
    </div>
  );
};

export const LinkBoardRuntime = PublicMicrositeApp;
export default PublicMicrositeApp;
